import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ErrorMessage } from "@/components/ErrorMessage";
import { AutomationStepKindPill } from "@/components/automations/AutomationStepKindPill";
import { AutomationStepNode } from "@/components/automations/AutomationStepNode";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormLimitsData,
    type WorkflowEditFormTriggerData,
} from "@/components/automations/automation-edit-form-data";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { useMembersByIds } from "@/queries/organization-queries";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { pullRequestEventName } from "@/utils/automations";
import { formatCronToReadable } from "@/utils/cron";
import { pluralizeWithCount } from "@/utils/strings";
import { getUserId } from "@/utils/subjects";
import type { PlainMessage } from "@bufbuild/protobuf";
import { IconPlayCircle } from "central-icons/IconPlayCircle";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { type FC } from "react";

type AutomationTriggerNodeProps = {
    limits?: WorkflowEditFormLimitsData;
    trigger?: WorkflowEditFormTriggerData;
    executor?: PlainMessage<Subject>;
    selected?: boolean;
    onClick?: () => void;
};

export const AutomationTriggerNode: FC<AutomationTriggerNodeProps> = ({
    limits,
    trigger,
    executor,
    selected = false,
    onClick,
}) => {
    const getVariant = () => {
        if (selected) return "selected";
        if (onClick) return "editable";
        return "readOnly";
    };

    if (!trigger) {
        return (
            <li
                className="w-full"
                onClick={onClick}
                data-tracking-id="select-empty-automation-trigger-node-automation-step-list"
            >
                <AutomationStepNode variant={getVariant()} className="px-4 py-3.5">
                    <div className="flex flex-1 flex-col items-start gap-1.5">
                        <AutomationStepKindPill className="mb-2" variant="default" LeadingIcon={IconPlayCircle}>
                            Trigger
                        </AutomationStepKindPill>
                    </div>
                </AutomationStepNode>
            </li>
        );
    }

    let triggerBadgeLabel: string;
    switch (trigger.type) {
        case "manual":
            triggerBadgeLabel = "Manual trigger";
            break;
        case "time": {
            const cronExpression = trigger.config?.time?.cronExpression;
            if (cronExpression) {
                triggerBadgeLabel = formatCronToReadable(cronExpression) ?? "Scheduled trigger";
            } else {
                triggerBadgeLabel = "Scheduled trigger";
            }
            break;
        }
        case "pullRequest":
            triggerBadgeLabel = "Pull request trigger";
            break;
        default:
            triggerBadgeLabel = "Trigger";
            break;
    }

    return (
        <li className="w-full" onClick={onClick} data-tracking-id="select-automation-trigger-node-automation-step-list">
            <AutomationStepNode
                variant={getVariant()}
                className={cn("py-3.5 pl-4 pr-4", { "cursor-pointer": !!onClick && !selected })}
            >
                <div className="flex flex-1 flex-col items-start gap-2">
                    <AutomationStepKindPill variant="default" LeadingIcon={IconPlayCircle}>
                        {triggerBadgeLabel}
                    </AutomationStepKindPill>
                    <TriggerContextLabel context={trigger.context} />
                    <TriggerPullRequestEventsLabel trigger={trigger} />
                    <TriggerLimitsLabel limits={limits} />
                    <ExecutorLabel executor={executor} className="pt-1" />
                </div>
            </AutomationStepNode>
        </li>
    );
};

const TriggerContextLabel: FC<{ context: WorkflowEditFormTriggerData["context"] }> = ({ context }) => {
    if (!context) {
        return null;
    }

    switch (context.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            return (
                <Text className="text-base">
                    <span>Runs across </span>
                    <Pill variant="default">{pluralizeWithCount(context.projectIds.length, false, "project")}</Pill>
                </Text>
            );
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            return (
                <Text className="text-base">
                    <span>Runs across </span>
                    <Pill variant="default">
                        {pluralizeWithCount(context.repoUrls.length, false, "repository", "repositories")}
                    </Pill>
                </Text>
            );
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
            return (
                <Text className="text-base">
                    <span>Runs for repositories matching </span>
                    {context.repoSearchString ? (
                        <Pill variant="default">{context.repoSearchString}</Pill>
                    ) : (
                        <span className="font-mono text-content-muted">-</span>
                    )}
                </Text>
            );
        default:
            return null;
    }
};

const TriggerPullRequestEventsLabel: FC<{ trigger: WorkflowEditFormTriggerData }> = ({ trigger }) => {
    if (trigger.type !== "pullRequest") {
        return null;
    }

    const events = trigger.config?.pullRequest?.events;
    if (!events || events.length === 0) {
        return null;
    }

    return (
        <Text className="text-base">
            <span>Triggers when a PR is: </span>
            <span className="inline-flex flex-wrap gap-1">
                {events.map((event) => (
                    <Pill key={event} variant="default" className="font-mono">
                        {pullRequestEventName(event)}
                    </Pill>
                ))}
            </span>
        </Text>
    );
};

const TriggerLimitsLabel: FC<{ limits?: WorkflowEditFormLimitsData }> = ({ limits }) => {
    if (!limits) {
        return null;
    }

    const maxParallel = limits.maxParallel ?? 10;
    const maxTotal = limits.maxTotal ?? 100;

    return (
        <Text className="text-base">
            <span className="font-medium">{maxParallel} concurrent</span>
            <span> and </span>
            <span className="font-medium">
                <span>Max </span>
                {pluralizeWithCount(maxTotal, false, "action")}
            </span>
            <span> per batch</span>
        </Text>
    );
};

const ExecutorLabel: FC<PropsWithClassName<{ executor?: PlainMessage<Subject> }>> = ({ executor, className }) => {
    const executorUserId = getUserId(executor);
    const {
        members: membersMap,
        isLoading: isLoadingMembers,
        isError: isMembersError,
    } = useMembersByIds(executorUserId ? [executorUserId] : [], {
        enabled: !!executorUserId,
    });
    const {
        data: serviceAccountsData,
        isLoading: isLoadingServiceAccounts,
        error: listServiceAccountsError,
    } = useListServiceAccounts({
        enabled: executor?.principal === Principal.SERVICE_ACCOUNT,
    });

    if (!executor) {
        return null;
    }

    const error = (isMembersError ? new Error("Failed to fetch members") : null) || listServiceAccountsError;
    if (error) {
        return <ErrorMessage error={error} />;
    }

    if (isLoadingMembers || isLoadingServiceAccounts) {
        return (
            <div className="flex items-center gap-1.5">
                <Text className="text-base">Runs as</Text>
                <SkeletonBlock ready={false} />
            </div>
        );
    }

    let name = "";
    let avatar: React.ReactNode = null;

    if (executor.principal === Principal.USER) {
        const executorMember = membersMap.get(executor.id);
        if (executorMember) {
            name = executorMember.fullName;
            avatar = (
                <Avatar size={16}>
                    <AvatarImage src={executorMember.avatarUrl} alt={executorMember.fullName} />
                    <AvatarFallback>
                        <AvatarInitials name={executorMember.fullName} size={16} />
                    </AvatarFallback>
                </Avatar>
            );
        }
    } else if (executor.principal === Principal.SERVICE_ACCOUNT && serviceAccountsData) {
        const serviceAccount = serviceAccountsData.serviceAccounts.find((sa) => sa.id === executor.id);
        if (serviceAccount) {
            name = serviceAccount.name;
            avatar = <ServiceAccountAvatar id={serviceAccount.id} size={16} />;
        }
    }

    if (!name) {
        return null;
    }

    return (
        <Text className={cn("flex items-center gap-1.5 text-base text-content-strong", className)}>
            <span>Runs as</span>
            {avatar && <span>{avatar}</span>}
            <span>{name}</span>
        </Text>
    );
};
